'use client';

import { Select } from '@base-ui-components/react/select';
import { Tabs } from '@base-ui-components/react/tabs';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FieldError, FieldErrors } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import {
  useAddress,
  useAddressByOrder,
  useContacts,
  useCreateComplaint,
  useCustomerNumber,
  useMultiFileUpload,
  useOrder,
} from '@/lib/service-api';
import { transformFormDataToComplaint } from '@/lib/transformations/complaint-form-to-api';
import { twMerge } from '@/tailwind-merge.config';
import type { AddressType, Contact, UploadFile } from '@/types/service-api';

import type { ComplaintArticleData } from './ComplaintArticleRow';
import { ComplaintArticlesList } from './ComplaintArticlesList';
import { ContactSelectDialog } from './ContactSelectDialog';
import { FileUploadDropzone } from './FileUploadDropzone';

function validateArticleErrors(
  article: ComplaintArticleData,
  t: (key: string, options?: Record<string, unknown>) => string
): string[] {
  const errors: string[] = [];
  const max = Number(article.originalQuantity);
  const num = Number(article.quantity);
  if (!article.quantity || article.quantity.trim() === '') {
    errors.push(t('complaint.validation.quantity_required'));
  } else if (isNaN(num)) {
    errors.push(t('complaint.validation.numbers_only'));
  } else if (num <= 0) {
    errors.push(t('complaint.validation.quantity_min'));
  } else if (!isNaN(max) && num > max) {
    errors.push(t('complaint.validation.quantity_exceeds_order', { max }));
  }
  if (!article.errorDescription || article.errorDescription.trim() === '') {
    errors.push(t('complaint.validation.error_description_required'));
  }
  return errors;
}

interface ComplaintFormData {
  // Auftragskopfdaten
  orderNr: number | string;
  orderSubNr: number | string;
  commission: string;
  description: string;

  // Fachhändler
  dealerNumber: string;
  dealerName: string;
  dealerStreet: string;
  dealerCountry: string;
  dealerPostalCode: string;
  dealerCity: string;
  dealerContactName: string;
  dealerContactPhone: string;
  dealerContactMobile: string;
  dealerContactEmail: string;

  // Lieferadresse
  deliveryCompletionLocation: string;
  deliveryName: string;
  deliveryStreet: string;
  deliveryCountry: string;
  deliveryPostalCode: string;
  deliveryCity: string;
  deliveryContactName: string;
  deliveryContactPhone: string;
  deliveryContactLocation: string;
}

interface ComplaintFormProps {
  onSuccess?: (complaintId?: number) => void;
  initialData?: Partial<ComplaintFormData>;
  className?: string;
}

const CUSTOMER_TYPE_KEYS = ['dealer', 'customer', 'warehouse', 'unclear'] as const;

const ADDRESS_TYPE_MAP: Record<string, AddressType> = {
  dealer: 'FH',
  customer: 'EK',
  warehouse: 'L',
  unclear: 'U',
};

export function ComplaintForm({
  onSuccess,
  initialData,
  className,
}: ComplaintFormProps) {
  const { t } = useTranslation('common');

  // Build translated customer type options
  const CUSTOMER_TYPE_OPTIONS = CUSTOMER_TYPE_KEYS.map((key) => ({
    value: key,
    label: t(
      `complaint.completion_types.${key === 'customer' ? 'end_customer' : key}`
    ),
  }));

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    trigger,
    clearErrors,
  } = useForm<ComplaintFormData>({
    defaultValues: {
      orderNr: initialData?.orderNr ?? ('' as any),
      orderSubNr: initialData?.orderSubNr ?? ('' as any),
      commission: initialData?.commission || '',
      description: initialData?.description || '',
      dealerNumber: initialData?.dealerNumber || '',
      dealerName: initialData?.dealerName || '',
      dealerStreet: initialData?.dealerStreet || '',
      dealerCountry: initialData?.dealerCountry || '',
      dealerPostalCode: initialData?.dealerPostalCode || '',
      dealerCity: initialData?.dealerCity || '',
      dealerContactName: initialData?.dealerContactName || '',
      dealerContactPhone: initialData?.dealerContactPhone || '',
      dealerContactMobile: initialData?.dealerContactMobile || '',
      dealerContactEmail: initialData?.dealerContactEmail || '',
      deliveryCompletionLocation: initialData?.deliveryCompletionLocation || '',
      deliveryName: initialData?.deliveryName || '',
      deliveryStreet: initialData?.deliveryStreet || '',
      deliveryCountry: initialData?.deliveryCountry || '',
      deliveryPostalCode: initialData?.deliveryPostalCode || '',
      deliveryCity: initialData?.deliveryCity || '',
      deliveryContactName: initialData?.deliveryContactName || '',
      deliveryContactPhone: initialData?.deliveryContactPhone || '',
      deliveryContactLocation: initialData?.deliveryContactLocation || '',
    },
  });

  const [articles, setArticles] = useState<ComplaintArticleData[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [articleValidationError, setArticleValidationError] = useState<string>('');
  const [articleFieldErrors, setArticleFieldErrors] = useState<
    Map<string, string[]>
  >(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedComplaintId, setSubmittedComplaintId] = useState<
    number | undefined
  >();
  // File Upload State
  const [pendingFiles, setPendingFiles] = useState<UploadFile[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { uploadAll, results: uploadResults, isUploading } = useMultiFileUpload();

  // API Integration State
  const [loadOrderEnabled, setLoadOrderEnabled] = useState(false);
  const [deliveryAddressError, setDeliveryAddressError] = useState<string | null>(
    null
  );

  // Watch order fields
  const orderNr = watch('orderNr');
  const orderSubNr = watch('orderSubNr');
  const completionLocation = watch('deliveryCompletionLocation');

  // Clear conditional field errors when switching away from "customer"
  useEffect(() => {
    if (completionLocation !== 'customer') {
      clearErrors([
        'deliveryContactName',
        'deliveryContactPhone',
        'deliveryContactLocation',
      ]);
    }
  }, [completionLocation, clearErrors]);

  // API Hooks
  const {
    data: orderData,
    loading: orderLoading,
    error: orderError,
    refetch: refetchOrder,
  } = useOrder(
    orderNr ? Number(orderNr) : 0,
    orderSubNr !== undefined && orderSubNr !== '' ? Number(orderSubNr) : 0,
    loadOrderEnabled
  );

  // Complaint Creation Hook
  const {
    mutate: createComplaint,
    loading: createComplaintLoading,
    error: createComplaintError,
    data: createdComplaint,
  } = useCreateComplaint();

  // Contact Selection State
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [contactFieldsEditable, setContactFieldsEditable] = useState(false);

  // Get customer number from session
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { customerNumber, loading: sessionLoading } = useCustomerNumber();

  // Load contacts
  const {
    data: contacts,
    loading: contactsLoading,
    error: contactsError,
  } = useContacts(customerNumber || '', !!customerNumber);

  // Load dealer address
  const {
    data: dealerAddress,
    loading: dealerLoading,
    error: dealerError,
  } = useAddress(customerNumber ? parseInt(customerNumber) : 0, !!customerNumber);

  // Load delivery address by order and address type
  const mappedAddressType: AddressType = completionLocation
    ? ADDRESS_TYPE_MAP[completionLocation]
    : 'FH';

  const {
    data: deliveryAddressData,
    loading: deliveryAddressLoading,
    error: deliveryAddressApiError,
    refetch: refetchDeliveryAddress,
  } = useAddressByOrder(
    orderNr ? Number(orderNr) : 0,
    orderSubNr !== undefined && orderSubNr !== '' ? Number(orderSubNr) : 0,
    mappedAddressType,
    false // Nicht automatisch laden - wir triggern manuell bei Dropdown-Änderung
  );

  // Load Order Handler
  const handleLoadOrder = () => {
    if (
      !orderNr ||
      orderNr === '' ||
      orderSubNr === undefined ||
      orderSubNr === ''
    ) {
      return;
    }
    if (loadOrderEnabled) {
      refetchOrder();
    } else {
      setLoadOrderEnabled(true);
    }
  };

  // Auto-load on blur (optional)
  const handleOrderFieldBlur = () => {
    if (
      orderNr &&
      orderNr !== '' &&
      orderSubNr !== undefined &&
      orderSubNr !== '' &&
      !loadOrderEnabled
    ) {
      handleLoadOrder();
    }
  };

  // Contact Selection Handlers
  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
    setValue('dealerContactName', contact.name);
    setValue('dealerContactPhone', contact.telNr || '');
    setValue('dealerContactMobile', contact.mobileNr || '');
    setValue('dealerContactEmail', contact.mail || '');
    setContactFieldsEditable(false);
  };

  const handleCustomContact = () => {
    setContactFieldsEditable(true);
  };

  // Map Order Data to Form
  useEffect(() => {
    if (orderData && !orderLoading) {
      setValue('commission', orderData.header.commission);
    }
  }, [orderData, orderLoading, setValue]);

  // Auto-fill first contact on load
  useEffect(() => {
    if (contacts && contacts.length > 0 && !selectedContact) {
      const firstContact = contacts[0];
      setSelectedContact(firstContact);
      setValue('dealerContactName', firstContact.name);
      setValue('dealerContactPhone', firstContact.telNr || '');
      setValue('dealerContactMobile', firstContact.mobileNr || '');
      setValue('dealerContactEmail', firstContact.mail || '');
    }
  }, [contacts, selectedContact, setValue]);

  // Auto-fill dealer address from API
  useEffect(() => {
    if (dealerAddress && customerNumber) {
      setValue('dealerNumber', customerNumber);
      setValue('dealerName', dealerAddress.name);
      setValue('dealerStreet', dealerAddress.street);
      setValue('dealerCountry', dealerAddress.country);
      setValue('dealerPostalCode', dealerAddress.postCode);
      setValue('dealerCity', dealerAddress.city);
    }
  }, [dealerAddress, customerNumber, setValue]);

  // Fetch delivery address when address type is selected
  useEffect(() => {
    if (
      completionLocation &&
      orderNr &&
      orderSubNr !== undefined &&
      orderSubNr !== ''
    ) {
      setDeliveryAddressError(null);
      refetchDeliveryAddress();
    }
  }, [completionLocation, orderNr, orderSubNr, refetchDeliveryAddress]);

  // Handle delivery address API response
  useEffect(() => {
    if (deliveryAddressData && !deliveryAddressLoading) {
      setValue('deliveryName', deliveryAddressData.name);
      setValue('deliveryStreet', deliveryAddressData.street);
      setValue('deliveryCountry', deliveryAddressData.country);
      setValue('deliveryPostalCode', deliveryAddressData.postCode);
      setValue('deliveryCity', deliveryAddressData.city);
      setDeliveryAddressError(null);
    } else if (deliveryAddressApiError && !deliveryAddressLoading) {
      // Clear fields and show error
      setValue('deliveryName', '');
      setValue('deliveryStreet', '');
      setValue('deliveryCountry', '');
      setValue('deliveryPostalCode', '');
      setValue('deliveryCity', '');
      setDeliveryAddressError(t('complaint.errors.no_address'));
    }
  }, [
    deliveryAddressData,
    deliveryAddressLoading,
    deliveryAddressApiError,
    setValue,
    t,
  ]);

  const handleFormSubmission = async (data: ComplaintFormData) => {
    // Prevent double submission
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Filter articles to only include selected ones
      const selectedArticles = articles.filter((article) => article.isSelected);

      // Validate that at least one article is selected
      if (selectedArticles.length === 0) {
        setArticleValidationError(t('complaint.validation.select_article'));
        setActiveTab(1);
        return;
      }

      // Validate that all selected articles have errorDescription filled
      const newArticleFieldErrors = new Map<string, string[]>();
      for (const article of selectedArticles) {
        const errors = validateArticleErrors(article, t);
        if (errors.length > 0) {
          newArticleFieldErrors.set(article.id, errors);
        }
      }

      if (newArticleFieldErrors.size > 0) {
        setArticleFieldErrors(newArticleFieldErrors);
        setArticleValidationError(
          t('complaint.validation.article_fields_incomplete')
        );
        setActiveTab(1);
        return;
      }

      // Clear article validation errors if valid
      setArticleFieldErrors(new Map());
      setArticleValidationError('');

      // Transform form data to API format
      const complaint = transformFormDataToComplaint(data, selectedArticles);

      await createComplaint(complaint);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle successful complaint creation and file upload
  useEffect(() => {
    if (createdComplaint && !createComplaintError && !createComplaintLoading) {
      // Extract complaint ID if available
      const complaintId = createdComplaint.header?.complaintId;

      // Upload files if any are pending
      if (complaintId && pendingFiles.length > 0 && customerNumber) {
        // Update file statuses to uploading
        setPendingFiles((prev) =>
          prev.map((f) => ({ ...f, status: 'uploading' as const }))
        );

        // Upload all files
        const filesToUpload = pendingFiles.map((f) => f.file);
        uploadAll(complaintId, filesToUpload, customerNumber).then((results) => {
          // Update file statuses based on results
          setPendingFiles((prev) =>
            prev.map((file, index) => ({
              ...file,
              status: results[index]?.success ? 'success' : 'error',
              error: results[index]?.error,
              progress: 100,
            }))
          );
        });
      }

      setSubmittedComplaintId(complaintId);
      setShowSuccess(true);
      // Optional: keep backward compatibility for standalone pages
      onSuccess?.(complaintId);
    }
  }, [
    createdComplaint,
    createComplaintError,
    createComplaintLoading,
    customerNumber,
    pendingFiles.length,
    uploadAll,
  ]);

  // Reset submission state on error so user can retry
  useEffect(() => {
    if (createComplaintError) {
      setIsSubmitting(false);
    }
  }, [createComplaintError]);

  // Shared input styles
  const inputBaseStyles = twMerge(
    'w-full border border-gray-300 px-3 py-2 text-p1CopyMobile lg:text-p1CopyDesktop',
    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black',
    'disabled:cursor-not-allowed disabled:bg-gray-100'
  );

  const inputErrorStyles = 'border-red-500 focus:ring-red-500';
  const labelStyles =
    'block text-p2CopyMobile lg:text-p2CopyDesktop font-medium mb-1 uppercase';
  const errorTextStyles =
    'text-red-600 text-p2CopyMobile lg:text-p2CopyDesktop mt-1';

  // Helper to render error message
  const renderError = (error?: FieldError) => {
    if (!error) return null;
    return <div className={errorTextStyles}>{error.message}</div>;
  };

  // Field-to-Tab mapping: all form fields are on Tab 0
  const FIELD_TAB_MAP: Record<string, number> = {
    orderNr: 0,
    orderSubNr: 0,
    commission: 0,
    description: 0,
    dealerNumber: 0,
    dealerName: 0,
    dealerStreet: 0,
    dealerCountry: 0,
    dealerPostalCode: 0,
    dealerCity: 0,
    dealerContactName: 0,
    dealerContactPhone: 0,
    dealerContactMobile: 0,
    dealerContactEmail: 0,
    deliveryCompletionLocation: 0,
    deliveryName: 0,
    deliveryStreet: 0,
    deliveryCountry: 0,
    deliveryPostalCode: 0,
    deliveryCity: 0,
    deliveryContactName: 0,
    deliveryContactPhone: 0,
    deliveryContactLocation: 0,
  };

  // onInvalid handler: navigate to the first tab with errors
  const handleValidationError = useCallback(
    (fieldErrors: FieldErrors<ComplaintFormData>) => {
      const errorFields = Object.keys(fieldErrors);
      if (errorFields.length === 0) return;

      // Find the lowest tab number with errors
      let targetTab = 2;
      for (const field of errorFields) {
        const tab = FIELD_TAB_MAP[field] ?? 0;
        if (tab < targetTab) {
          targetTab = tab;
        }
      }

      setActiveTab(targetTab);
    },
    []
  );

  // Compute which tabs have errors (for error indicators)
  const tabsWithErrors = useMemo(() => {
    const tabs = new Set<number>();

    // Form field errors → Tab 0
    const errorFields = Object.keys(errors);
    for (const field of errorFields) {
      const tab = FIELD_TAB_MAP[field] ?? 0;
      tabs.add(tab);
    }

    // Article validation errors → Tab 1
    if (articleValidationError || articleFieldErrors.size > 0) {
      tabs.add(1);
    }

    return tabs;
  }, [errors, articleValidationError, articleFieldErrors]);

  return (
    <div className={twMerge('mx-auto max-w-screen-desktop', className)}>
      {showSuccess ? (
        // SUCCESS MESSAGE
        <div className="mx-auto max-w-3xl py-12">
          {/* Success Icon */}
          <div className="mb-8 flex justify-center">
            <div className="flex size-24 items-center justify-center rounded-full bg-green-100">
              <svg
                className="size-12 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center">
            <h2 className="mb-4 text-h2Mobile lg:text-h2Desktop">
              {t('complaint.success.title')}
            </h2>
            <p className="text-p1CopyMobile text-grau lg:text-p1CopyDesktop">
              {t('complaint.success.message')}
            </p>

            {/* Complaint ID if available */}
            {submittedComplaintId && (
              <div className="mt-6 rounded-lg bg-gray-50 p-6">
                <p className="text-p2CopyMobile uppercase text-grau lg:text-p2CopyDesktop">
                  {t('complaint.success.complaint_number')}
                </p>
                <p className="mt-2 text-h3Mobile font-bold lg:text-h3Desktop">
                  {submittedComplaintId}
                </p>
              </div>
            )}

            {/* Information Box */}
            <div className="mt-8 rounded-lg border border-gray-300 bg-gray-50 p-6 text-left">
              <h3 className="mb-4 text-h5Mobile uppercase lg:text-h5Desktop">
                {t('complaint.success.next_steps_title')}
              </h3>
              <ul className="space-y-3 text-p1CopyMobile text-grau lg:text-p1CopyDesktop">
                <li className="flex gap-3">
                  <span className="text-black">•</span>
                  <span>{t('complaint.success.next_steps_1')}</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-black">•</span>
                  <span>{t('complaint.success.next_steps_2')}</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-black">•</span>
                  <span>{t('complaint.success.next_steps_3')}</span>
                </li>
              </ul>
            </div>

            {/* Reset Button */}
            <button
              type="button"
              onClick={() => {
                setShowSuccess(false);
                setSubmittedComplaintId(undefined);
                reset();
                setArticles([]);
                setPendingFiles([]);
                setActiveTab(0);
              }}
              className="btn btn--primary mt-8 uppercase"
            >
              {t('complaint.buttons.new_complaint')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="mb-4 text-h2Mobile lg:text-h2Desktop">
              {t('complaint.page_title')}
            </h1>
            <p className="text-p1CopyMobile text-grau lg:text-p1CopyDesktop">
              {t('complaint.required_fields_note')}
            </p>
          </div>

          {/* Tabs */}
          <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <Tabs.List className="mb-8 flex gap-4">
              <Tabs.Tab
                value={0}
                className={twMerge(
                  'btn data-[selected]:btn--primary relative uppercase',
                  'data-[selected=false]:btn--white btn--white btn--icon btn--icon--arrow-left'
                )}
              >
                {t('complaint.tabs.order_data')}
                {tabsWithErrors.has(0) && (
                  <span className="absolute -right-1 -top-1 size-3 rounded-full bg-inperspective-rot" />
                )}
              </Tabs.Tab>
              <Tabs.Tab
                value={1}
                className={twMerge(
                  'btn data-[selected]:btn--primary relative uppercase',
                  'data-[selected=false]:btn--white btn--icon btn--icon--arrow-left btn--white hover:btn--white hover:text-white'
                )}
              >
                {t('complaint.tabs.articles')}
                {tabsWithErrors.has(1) && (
                  <span className="absolute -right-1 -top-1 size-3 rounded-full bg-inperspective-rot" />
                )}
              </Tabs.Tab>
              <Tabs.Tab
                value={2}
                className={twMerge(
                  'btn data-[selected]:btn--primary relative uppercase',
                  'data-[selected=false]:btn--white btn--white btn--icon btn--icon--arrow-left'
                )}
              >
                {t('complaint.tabs.attachments')}
                {tabsWithErrors.has(2) && (
                  <span className="absolute -right-1 -top-1 size-3 rounded-full bg-inperspective-rot" />
                )}
              </Tabs.Tab>
            </Tabs.List>

            <form
              onSubmit={handleFormSubmit(
                handleFormSubmission,
                handleValidationError
              )}
              className="space-y-8"
            >
              {/* Tab Panel: Auftragsdaten */}
              <Tabs.Panel value={0}>
                {/* AUFTRAGSKOPFDATEN Fieldset */}
                <fieldset className="pb-8">
                  <legend className="mb-1 text-h3Mobile lg:text-h3Desktop">
                    {t('complaint.sections.order_header')}
                  </legend>
                  <div className="grid grid-cols-1 gap-6 border-t border-black pt-8 tablet:grid-cols-2">
                    <div className="grid grid-cols-1 gap-6 tablet:grid-cols-2">
                      {/* Order Number */}
                      <Controller
                        name="orderNr"
                        control={control}
                        rules={{
                          required: t('complaint.validation.order_number_required'),
                          validate: (value) => {
                            if (!value || value === '')
                              return t('complaint.validation.order_number_required');
                            if (!/^[0-9]+$/.test(String(value)))
                              return t('complaint.validation.numbers_only');
                            if (Number(value) <= 0)
                              return t('complaint.validation.invalid_order_number');
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <div>
                            <label htmlFor={field.name} className={labelStyles}>
                              {t('complaint.fields.order_number')} *
                            </label>
                            <input
                              {...field}
                              value={field.value ?? ''}
                              id={field.name}
                              type="text"
                              inputMode="numeric"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(value === '' ? '' : Number(value));
                              }}
                              onBlur={handleOrderFieldBlur}
                              className={twMerge(
                                inputBaseStyles,
                                errors.orderNr && inputErrorStyles
                              )}
                            />
                            {renderError(errors.orderNr)}
                          </div>
                        )}
                      />

                      {/* Order Sub Number + Load Button */}
                      <div>
                        <label htmlFor="orderSubNr" className={labelStyles}>
                          {t('complaint.fields.sub_order')} *
                        </label>
                        <div className="flex gap-2">
                          <Controller
                            name="orderSubNr"
                            control={control}
                            rules={{
                              required: t('complaint.validation.required'),
                              validate: (value) => {
                                if (!value && value !== 0 && value !== '')
                                  return t('complaint.validation.required');
                                if (value === '')
                                  return t('complaint.validation.required');
                                if (!/^[0-9]+$/.test(String(value)))
                                  return t('complaint.validation.numbers_only');
                                if (Number(value) < 0)
                                  return t('complaint.validation.invalid_number');
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <div className="flex-1">
                                <input
                                  {...field}
                                  value={field.value ?? ''}
                                  id={field.name}
                                  type="text"
                                  inputMode="numeric"
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    // Erlaube nur Zahlen, aber behalte den String (für führende Nullen wie "000")
                                    if (value === '' || /^[0-9]+$/.test(value)) {
                                      field.onChange(value);
                                    }
                                  }}
                                  onBlur={handleOrderFieldBlur}
                                  className={twMerge(
                                    inputBaseStyles,
                                    errors.orderSubNr && inputErrorStyles
                                  )}
                                />
                                {renderError(errors.orderSubNr)}
                              </div>
                            )}
                          />
                          <button
                            type="button"
                            onClick={handleLoadOrder}
                            disabled={orderLoading || !orderNr}
                            className={twMerge(
                              'whitespace-nowrap border border-black bg-black px-6 py-2 text-p1CopyMobile text-white transition-colors lg:text-p1CopyDesktop',
                              'hover:bg-gray-800',
                              'disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-50'
                            )}
                          >
                            {orderLoading
                              ? t('complaint.buttons.loading')
                              : t('complaint.buttons.load_order')}
                          </button>
                        </div>
                      </div>

                      {/* Commission */}
                      <div className="tablet:col-span-2">
                        <Controller
                          name="commission"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.commission')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* Loading State */}
                    {orderLoading && (
                      <div className="tablet:col-span-2">
                        <div className="flex items-center gap-3 rounded-lg bg-blue-50 p-4">
                          <div className="size-5 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600"></div>
                          <span className="text-p2CopyMobile text-blue-800 lg:text-p2CopyDesktop">
                            {t('complaint.loading.order_data')}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Error State */}
                    {orderError && !orderLoading && (
                      <div className="tablet:col-span-2">
                        <div className="rounded-lg bg-red-50 p-4">
                          <p className="text-p2CopyMobile font-semibold text-red-800 lg:text-p2CopyDesktop">
                            {t('complaint.errors.load_order')}
                          </p>
                          <p className="mt-1 text-p2CopyMobile text-red-600 lg:text-p2CopyDesktop">
                            {orderError}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Description Textarea */}
                    <div>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <label htmlFor={field.name} className={labelStyles}>
                              {t('complaint.fields.note')}
                            </label>
                            <textarea
                              {...field}
                              id={field.name}
                              className={twMerge(
                                inputBaseStyles,
                                'min-h-[100px] resize-y'
                              )}
                            />
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </fieldset>

                {/* FACHHÄNDLER Fieldset */}
                <fieldset className="pb-8">
                  <legend className="mb-1 text-h3Mobile lg:text-h3Desktop">
                    {t('complaint.sections.dealer')}
                  </legend>
                  <div className="grid grid-cols-1 gap-6 border-t border-black pt-8 tablet:grid-cols-2">
                    <div className="relative grid grid-cols-1 gap-6 pt-16 tablet:grid-cols-2">
                      {/* Loading Overlay */}
                      {dealerLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm">
                          <div className="flex flex-col items-center gap-3">
                            <div className="size-8 animate-spin rounded-full border-4 border-blue-300 border-t-blue-600"></div>
                            <span className="text-p1CopyMobile font-medium text-blue-800 lg:text-p1CopyDesktop">
                              {t('complaint.loading.dealer_address')}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Dealer Number */}
                      <Controller
                        name="dealerNumber"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <label htmlFor={field.name} className={labelStyles}>
                              {t('complaint.fields.number')}
                            </label>
                            <input
                              {...field}
                              id={field.name}
                              type="text"
                              disabled
                              className={inputBaseStyles}
                            />
                          </div>
                        )}
                      />

                      {/* Dealer Name */}
                      <div className="tablet:col-span-2">
                        <Controller
                          name="dealerName"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.name')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                disabled
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />
                      </div>

                      {/* Dealer Street */}
                      <div className="tablet:col-span-2">
                        <Controller
                          name="dealerStreet"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.street')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                disabled
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />
                      </div>

                      {/* Dealer Country, PLZ, City */}
                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 tablet:col-span-2">
                        {/* Dealer Country */}
                        <div>
                          <Controller
                            name="dealerCountry"
                            control={control}
                            render={({ field }) => (
                              <div>
                                <label htmlFor={field.name} className={labelStyles}>
                                  {t('complaint.fields.country')}
                                </label>
                                <input
                                  {...field}
                                  id={field.name}
                                  type="text"
                                  disabled
                                  className={inputBaseStyles}
                                />
                              </div>
                            )}
                          />
                        </div>

                        {/* Dealer Postal Code */}
                        <Controller
                          name="dealerPostalCode"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.postcode')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                disabled
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />

                        {/* Dealer City */}
                        <Controller
                          name="dealerCity"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.city')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                disabled
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />
                      </div>

                      {/* Dealer Address Error State */}
                      {dealerError && !dealerLoading && (
                        <div className="mt-4 rounded-lg bg-red-50 p-4">
                          <p className="text-p2CopyMobile font-semibold text-red-800 lg:text-p2CopyDesktop">
                            {t('complaint.errors.load_dealer')}
                          </p>
                          <p className="mt-1 text-p2CopyMobile text-red-600 lg:text-p2CopyDesktop">
                            {dealerError}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Ansprechpartner subsection */}
                    <div className="relative mt-6">
                      {/* Loading Overlay */}
                      {contactsLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm">
                          <div className="flex flex-col items-center gap-3">
                            <div className="size-8 animate-spin rounded-full border-4 border-blue-300 border-t-blue-600"></div>
                            <span className="text-p1CopyMobile font-medium text-blue-800 lg:text-p1CopyDesktop">
                              {t('complaint.loading.contact_data')}
                            </span>
                          </div>
                        </div>
                      )}

                      <h3 className="mb-4 text-buttonFont uppercase lg:text-buttonFont">
                        {t('complaint.sections.contact_person')}
                      </h3>
                      <div className="grid grid-cols-1 gap-6 pt-1 tablet:grid-cols-2">
                        {/* Dealer Contact Name */}
                        <div className="tablet:col-span-2">
                          <Controller
                            name="dealerContactName"
                            control={control}
                            render={({ field }) => (
                              <div>
                                <label htmlFor={field.name} className={labelStyles}>
                                  {t('complaint.fields.name')}
                                </label>
                                <input
                                  {...field}
                                  id={field.name}
                                  type="text"
                                  disabled={!contactFieldsEditable}
                                  className={inputBaseStyles}
                                />
                              </div>
                            )}
                          />
                        </div>

                        {/* Dealer Contact Phone */}
                        <Controller
                          name="dealerContactPhone"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.phone')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="tel"
                                disabled={!contactFieldsEditable}
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />

                        {/* Dealer Contact Mobile */}
                        <Controller
                          name="dealerContactMobile"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.mobile')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="tel"
                                disabled={!contactFieldsEditable}
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />

                        {/* Dealer Contact Email */}
                        <div className="tablet:col-span-2">
                          <Controller
                            name="dealerContactEmail"
                            control={control}
                            render={({ field }) => (
                              <div>
                                <label htmlFor={field.name} className={labelStyles}>
                                  {t('complaint.fields.email')}
                                </label>
                                <input
                                  {...field}
                                  id={field.name}
                                  type="email"
                                  disabled={!contactFieldsEditable}
                                  className={inputBaseStyles}
                                />
                              </div>
                            )}
                          />
                        </div>

                        {/* Contact Select Button */}
                        <div className="tablet:col-span-2">
                          <button
                            type="button"
                            onClick={() => setIsContactDialogOpen(true)}
                            disabled={
                              contactsLoading || !contacts || contacts.length === 0
                            }
                            className={twMerge(
                              'w-full border border-black bg-white px-6 py-3 text-p1CopyMobile uppercase text-black transition-colors hover:bg-gray-100 lg:text-p1CopyDesktop',
                              'disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                          >
                            {contactsLoading
                              ? t('complaint.buttons.loading')
                              : t('complaint.buttons.select_contact')}
                          </button>
                        </div>

                        {/* Contacts Error State */}
                        {contactsError && !contactsLoading && (
                          <div className="tablet:col-span-2">
                            <div className="rounded-lg bg-red-50 p-4">
                              <p className="text-p2CopyMobile font-semibold text-red-800 lg:text-p2CopyDesktop">
                                {t('complaint.errors.load_contact')}
                              </p>
                              <p className="mt-1 text-p2CopyMobile text-red-600 lg:text-p2CopyDesktop">
                                {contactsError}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </fieldset>

                {/* Contact Select Dialog */}
                <ContactSelectDialog
                  open={isContactDialogOpen}
                  contacts={contacts || []}
                  onSelectContact={handleSelectContact}
                  onClose={() => setIsContactDialogOpen(false)}
                  onCustomContact={handleCustomContact}
                />

                {/* LIEFERADRESSE Fieldset */}
                <fieldset className="pb-8">
                  <legend className="mb-1 text-h3Mobile lg:text-h3Desktop">
                    {t('complaint.sections.delivery_address')}
                  </legend>
                  <div className="grid grid-cols-1 gap-6 border-t border-black pt-8 tablet:grid-cols-2">
                    <div className="grid grid-cols-1 gap-6 pt-16 tablet:grid-cols-2">
                      {/* Delivery Completion Location Select */}
                      <div className="tablet:col-span-2">
                        <Controller
                          name="deliveryCompletionLocation"
                          control={control}
                          rules={{
                            required: t('complaint.validation.name_street_missing'),
                          }}
                          render={({ field }) => (
                            <div>
                              <label className={labelStyles}>
                                {t('complaint.fields.completion_at')}
                              </label>
                              <Select.Root
                                value={field.value || null}
                                onValueChange={field.onChange}
                              >
                                <Select.Trigger
                                  className={twMerge(
                                    inputBaseStyles,
                                    'flex justify-between',
                                    errors.deliveryCompletionLocation &&
                                      inputErrorStyles
                                  )}
                                >
                                  <Select.Value>
                                    {(value) => {
                                      const option = CUSTOMER_TYPE_OPTIONS.find(
                                        (opt) => opt.value === value
                                      );
                                      return option ? option.label : '';
                                    }}
                                  </Select.Value>
                                  <Select.Icon>▼</Select.Icon>
                                </Select.Trigger>
                                <Select.Portal>
                                  <Select.Positioner sideOffset={4}>
                                    <Select.Popup className="z-50 max-h-60 overflow-auto rounded border border-gray-300 bg-white shadow-lg">
                                      {CUSTOMER_TYPE_OPTIONS.map((option) => (
                                        <Select.Item
                                          key={option.value}
                                          value={option.value}
                                          className="cursor-pointer px-3 py-2 hover:bg-gray-100 data-[highlighted]:bg-gray-100"
                                        >
                                          <Select.ItemText>
                                            {option.label}
                                          </Select.ItemText>
                                          <Select.ItemIndicator className="ml-2">
                                            ✓
                                          </Select.ItemIndicator>
                                        </Select.Item>
                                      ))}
                                    </Select.Popup>
                                  </Select.Positioner>
                                </Select.Portal>
                              </Select.Root>
                              {renderError(errors.deliveryCompletionLocation)}
                              {deliveryAddressLoading && (
                                <p className="mt-1 text-sm text-gray-500">
                                  {t('complaint.loading.address')}
                                </p>
                              )}
                              {deliveryAddressError && !deliveryAddressLoading && (
                                <p className="mt-1 text-sm text-red-600">
                                  {deliveryAddressError}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>

                      {/* Delivery Name */}
                      <div className="tablet:col-span-2">
                        <Controller
                          name="deliveryName"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.name')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />
                      </div>

                      {/* Delivery Street */}
                      <div className="tablet:col-span-2">
                        <Controller
                          name="deliveryStreet"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.street')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />
                      </div>

                      {/* Delivery Country, PLZ, City */}
                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 tablet:col-span-2">
                        {/* Delivery Country */}
                        <div>
                          <Controller
                            name="deliveryCountry"
                            control={control}
                            render={({ field }) => (
                              <div>
                                <label htmlFor={field.name} className={labelStyles}>
                                  {t('complaint.fields.country')}
                                </label>
                                <input
                                  {...field}
                                  id={field.name}
                                  type="text"
                                  className={inputBaseStyles}
                                />
                              </div>
                            )}
                          />
                        </div>

                        {/* Delivery Postal Code */}
                        <Controller
                          name="deliveryPostalCode"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.postcode')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />

                        {/* Delivery City */}
                        <Controller
                          name="deliveryCity"
                          control={control}
                          render={({ field }) => (
                            <div>
                              <label htmlFor={field.name} className={labelStyles}>
                                {t('complaint.fields.city')}
                              </label>
                              <input
                                {...field}
                                id={field.name}
                                type="text"
                                className={inputBaseStyles}
                              />
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* Ansprechpartner subsection */}
                    {completionLocation === 'customer' && (
                      <div className="mt-6">
                        <h3 className="mb-4 text-h5Mobile uppercase lg:text-h5Desktop">
                          {t('complaint.sections.contact_person')}
                        </h3>
                        <div className="grid grid-cols-1 gap-6 tablet:grid-cols-1">
                          {/* Delivery Contact Name */}
                          <div className="">
                            <Controller
                              name="deliveryContactName"
                              control={control}
                              rules={{
                                required:
                                  completionLocation === 'customer'
                                    ? t('complaint.validation.name_required')
                                    : false,
                              }}
                              render={({ field }) => (
                                <div>
                                  <label
                                    htmlFor={field.name}
                                    className={labelStyles}
                                  >
                                    {t('complaint.fields.name')} *
                                  </label>
                                  <input
                                    {...field}
                                    id={field.name}
                                    type="text"
                                    className={twMerge(
                                      inputBaseStyles,
                                      errors.deliveryContactName && inputErrorStyles
                                    )}
                                  />
                                  {renderError(errors.deliveryContactName)}
                                </div>
                              )}
                            />
                          </div>

                          {/* Delivery Contact Phone */}
                          <Controller
                            name="deliveryContactPhone"
                            control={control}
                            rules={{
                              required:
                                completionLocation === 'customer'
                                  ? t('complaint.validation.phone_required')
                                  : false,
                            }}
                            render={({ field }) => (
                              <div>
                                <label htmlFor={field.name} className={labelStyles}>
                                  {t('complaint.fields.phone')} *
                                </label>
                                <input
                                  {...field}
                                  id={field.name}
                                  type="tel"
                                  className={twMerge(
                                    inputBaseStyles,
                                    errors.deliveryContactPhone && inputErrorStyles
                                  )}
                                />
                                {renderError(errors.deliveryContactPhone)}
                              </div>
                            )}
                          />

                          {/* Delivery Contact Location */}
                          <Controller
                            name="deliveryContactLocation"
                            control={control}
                            rules={{
                              required:
                                completionLocation === 'customer'
                                  ? t('complaint.validation.building_room_required')
                                  : false,
                            }}
                            render={({ field }) => (
                              <div>
                                <label htmlFor={field.name} className={labelStyles}>
                                  {t('complaint.fields.building_room')} *
                                </label>
                                <input
                                  {...field}
                                  id={field.name}
                                  type="text"
                                  className={twMerge(
                                    inputBaseStyles,
                                    errors.deliveryContactLocation &&
                                      inputErrorStyles
                                  )}
                                />
                                {renderError(errors.deliveryContactLocation)}
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>

                {/* Form Actions */}
                <div className="flex justify-end pt-6">
                  <button
                    type="button"
                    onClick={async () => {
                      const tab0Fields: (keyof ComplaintFormData)[] = [
                        'orderNr',
                        'orderSubNr',
                        'deliveryCompletionLocation',
                      ];
                      if (completionLocation === 'customer') {
                        tab0Fields.push(
                          'deliveryContactName',
                          'deliveryContactPhone',
                          'deliveryContactLocation'
                        );
                      }
                      const isValid = await trigger(tab0Fields);
                      if (!isValid) return;
                      setActiveTab(1);
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 100);
                    }}
                    className="btn btn--white btn--icon btn--icon--arrow-left w-fit shrink-0 uppercase"
                  >
                    {t('complaint.buttons.next')}
                  </button>
                </div>
              </Tabs.Panel>

              {/* Tab Panel: Artikel */}
              <Tabs.Panel value={1}>
                {/* Article Validation Error */}
                {articleValidationError && (
                  <div className="mb-6 rounded-lg border-2 border-inperspective-rot bg-red-50 p-4">
                    <p className="text-p1CopyMobile font-semibold text-inperspective-rot lg:text-p1CopyDesktop">
                      {articleValidationError}
                    </p>
                  </div>
                )}

                <ComplaintArticlesList
                  initialArticles={articles}
                  orderArticles={orderData?.orderItems || []}
                  articleFieldErrors={articleFieldErrors}
                  onChange={(newArticles) => {
                    setArticles(newArticles);
                    if (articleValidationError) {
                      setArticleValidationError('');
                    }
                    if (articleFieldErrors.size > 0) {
                      setArticleFieldErrors(new Map());
                    }
                  }}
                />

                {/* Form Actions */}
                <div
                  className={twMerge(
                    'flex justify-between pt-6',
                    articles.length > 5 &&
                      'sticky bottom-0 -mx-4 bg-white px-4 pb-4 pt-10 before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-10 before:bg-gradient-to-b before:from-white/0 before:to-white before:content-[""]'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setActiveTab(0)}
                    className="btn btn--white btn--icon btn--icon--arrow-back pl-4.5 pr-9 uppercase"
                  >
                    {t('complaint.buttons.back')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const selectedArticles = articles.filter((a) => a.isSelected);

                      if (selectedArticles.length === 0) {
                        setArticleValidationError(
                          t('complaint.validation.select_article')
                        );
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        return;
                      }

                      const newErrors = new Map<string, string[]>();
                      for (const article of selectedArticles) {
                        const articleErrors = validateArticleErrors(article, t);
                        if (articleErrors.length > 0) {
                          newErrors.set(article.id, articleErrors);
                        }
                      }

                      if (newErrors.size > 0) {
                        setArticleFieldErrors(newErrors);
                        setArticleValidationError(
                          t('complaint.validation.article_fields_incomplete')
                        );
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        return;
                      }

                      setArticleValidationError('');
                      setArticleFieldErrors(new Map());
                      setActiveTab(2);
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 100);
                    }}
                    className="btn btn--white btn--icon btn--icon--arrow-left w-fit shrink-0 uppercase"
                  >
                    {t('complaint.buttons.next')}
                  </button>
                </div>
              </Tabs.Panel>

              {/* Tab Panel: Anlage (Attachments) */}
              <Tabs.Panel value={2}>
                {/* API Error Display */}
                {createComplaintError && (
                  <div className="mb-6 rounded-lg border-2 border-red-500 bg-red-50 p-4">
                    <p className="text-p1CopyMobile font-semibold text-red-800 lg:text-p1CopyDesktop">
                      {t('complaint.errors.submit_failed')}
                    </p>
                    <p className="mt-1 text-p2CopyMobile text-red-600 lg:text-p2CopyDesktop">
                      {createComplaintError}
                    </p>
                  </div>
                )}

                {/* File Upload Dropzone */}
                <div className="pb-8">
                  <h3 className="mb-4 text-h3Mobile lg:text-h3Desktop">
                    {t('complaint.file_upload.title')}
                  </h3>
                  <p className="mb-6 text-p1CopyMobile text-grau lg:text-p1CopyDesktop">
                    {t('complaint.file_upload.description')}
                  </p>

                  <FileUploadDropzone
                    files={pendingFiles}
                    onFilesChange={setPendingFiles}
                    disabled={createComplaintLoading || isSubmitting || isUploading}
                    maxFiles={10}
                  />

                  {/* Info about upload timing */}
                  {pendingFiles.length > 0 && (
                    <p className="mt-4 text-p2CopyMobile text-grau lg:text-p2CopyDesktop">
                      {t('complaint.file_upload.upload_after_submit')}
                    </p>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex justify-between pt-6">
                  <button
                    type="button"
                    onClick={() => setActiveTab(1)}
                    className="btn btn--white btn--icon btn--icon--arrow-back pl-4.5 pr-9 uppercase"
                    disabled={createComplaintLoading || isSubmitting || isUploading}
                  >
                    {t('complaint.buttons.back')}
                  </button>
                  <button
                    type="submit"
                    className={twMerge(
                      'btn btn--white btn--icon btn--icon--arrow-left w-fit shrink-0 uppercase',
                      (createComplaintLoading || isSubmitting || isUploading) &&
                        'opacity-75'
                    )}
                    disabled={createComplaintLoading || isSubmitting || isUploading}
                  >
                    {createComplaintLoading || isSubmitting || isUploading ? (
                      <span className="flex items-center gap-2">
                        <span className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span>
                          {isUploading
                            ? t('complaint.buttons.uploading_files')
                            : t('complaint.buttons.submitting')}
                        </span>
                      </span>
                    ) : (
                      t('complaint.buttons.submit')
                    )}
                  </button>
                </div>
              </Tabs.Panel>
            </form>
          </Tabs.Root>
        </>
      )}
    </div>
  );
}
